import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { Tooltip } from 'components/Tooltip';
import type { EnrichedContactWithCrm } from 'domain/contact';
import type { CrmAccount } from 'domain/crm';
import { Crm } from 'domain/crm';
import { useHasVerticalScroll } from 'hooks/useHasVerticalScroll';
import { CrmExportPreviewAccountSelect } from 'pages/contacts/ContactItem/CrmExportPreviewModal/CrmExportPreviewAccountSelect';
import { translate } from 'services/i18n';

import { useCrmStatus } from '../../../../hooks/useCrmStatus';
import { AccountOwnerTooltip } from '../../ContactItem/AccountOwnerTooltip/AccountOwnerTooltip';

import {
  StyledBulkText,
  StyledContactList,
  StyledContactListColumn,
  StyledContactListHeaderRow,
  StyledContactListName,
  StyledContactListRow,
  StyledContactListText,
  StyledDivider,
} from './styles';

export const MatchingState = ({
  unmatchedContacts,
  matchedCount,
  total,
  crm,
  isOpen,
  onContactChange,
  contacts,
}: {
  unmatchedContacts: EnrichedContactWithCrm[];
  matchedCount: number;
  total: number;
  crm: Crm;
  onContactChange: (contact: EnrichedContactWithCrm, crmAccount: CrmAccount) => void;
  contacts: EnrichedContactWithCrm[];
  isOpen: boolean;
}) => {
  const { ref: contactListRef, hasScroll } = useHasVerticalScroll<HTMLDivElement>();
  const { isSfLabelsEnabled, isHbLabelsEnabled } = useCrmStatus();
  const isCrmLabelsEnabled = crm === Crm.salesforce ? isSfLabelsEnabled : isHbLabelsEnabled;

  return (
    <>
      <StyledDivider />
      <StyledBulkText>
        {translate('contacts.bulk.export_to_crm.preview_modal.subtitle', {
          matchedCount,
          total,
        })}
      </StyledBulkText>
      {isCrmLabelsEnabled ? null : <AccountOwnerTooltip crm={crm} />}
      <StyledContactListHeaderRow>
        <StyledContactListColumn>
          {`${translate('contacts.bulk.export_to_crm.preview_modal.contact_name')} (${unmatchedContacts.length})`}
        </StyledContactListColumn>
        <StyledContactListColumn>
          {translate('contacts.bulk.export_to_crm.preview_modal.account_name')}
        </StyledContactListColumn>
      </StyledContactListHeaderRow>

      <StyledContactList ref={contactListRef} enableScroll={hasScroll} isCrmLabelsEnabled={!!isCrmLabelsEnabled}>
        {contacts.map((contact) => {
          const isExistAsLead = contact.preview?.isExistAsLead;
          return (
            <StyledContactListRow key={contact.id} isDisabled={isExistAsLead}>
              <StyledContactListColumn>
                <StyledContactListName>
                  {contact.name}
                  {isExistAsLead ? (
                    <Tooltip
                      content={translate('contacts.bulk.export_to_crm.preview_modal.exist_as_lead.tooltip')}
                      placement="bottom-right"
                      minWidth={230}
                    >
                      <SWReactIcons iconName="alert-circle" size="xs" iconColor={colorsPalettes.red['s100']} />
                    </Tooltip>
                  ) : null}
                </StyledContactListName>
                <StyledContactListText>{`${contact.role} at ${contact.companyName}`}</StyledContactListText>
              </StyledContactListColumn>
              <StyledContactListColumn>
                <CrmExportPreviewAccountSelect
                  crm={crm}
                  companyName={contact.companyName}
                  isOpen={isOpen}
                  selectedAccount={contact.crmAccount || null}
                  setSelectedAccount={(account) => onContactChange(contact, account)}
                  matchedCompanies={contact.preview?.matchedCompanies || []}
                  dropdownPlacement="right"
                  isExistAsLead={isExistAsLead}
                />
              </StyledContactListColumn>
            </StyledContactListRow>
          );
        })}
      </StyledContactList>
    </>
  );
};
